import { useState } from "react";
import { Badge, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { BannerService } from "@services/banners/bannerService";
import type { CurrentBanner } from "@services/banners/commands/cmdGetCurrentBanner";
import TurnOffBannerModal from "@/modules/banners/components/TurnOffBannerModal";
import { bannerEditPath } from "@/modules/banners/bannerForm";
import showAlert from "src/shared/utils/generic.tsx";
import { liveWindowLabel } from "@/shared/utils/date-utils/easternTime";
import { Paths } from "@/paths";

// Server codes for a banner that is already down (expired, not live, or deleted).
const ALREADY_DOWN = new Set(["banner_expired", "banner_not_live", "not_found"]);

interface BannerControlsProps {
    banner: CurrentBanner;
    /** The banner is down: turned off here, or already down (banner_expired / banner_not_live / not_found). */
    onTurnedOff: () => void;
}

/** The Banner Managers' strip under the live banner on the Partner Dashboard (MPN-2266). */
export default function BannerControls({ banner, onTurnedOff }: BannerControlsProps) {
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);

    async function turnOff() {
        setBusy(true);
        try {
            await new BannerService().turnOffBanner(banner.uuid);
            setConfirming(false);
            onTurnedOff();
            showAlert("Banner turned off.", "success", 3000);
        } catch (e) {
            const code = (e as { code?: string }).code;
            setConfirming(false);
            // Already expired, not live or deleted: it is down either way. Anything else (403, 5xx) leaves it Live.
            if (code && ALREADY_DOWN.has(code)) onTurnedOff();
            else showAlert("Could not turn off the banner.", "error", 3000);
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="dashboard-banner__controls" role="region" aria-label="Banner controls">
            <Badge bg="" className="text-uppercase dashboard-banner__audience">Super Admins only</Badge>
            <Badge bg="success">Live</Badge>
            <span className="dashboard-banner__window">{liveWindowLabel(banner)}</span>
            <div className="ms-auto d-flex align-items-center gap-2">
                <Link className="btn btn-sm btn-neutral" to={bannerEditPath(banner.uuid)}>Edit</Link>
                <Button variant="danger" size="sm" onClick={() => setConfirming(true)}>Turn Off</Button>
                <Link className="fw-semibold" to={`${import.meta.env.BASE_URL}${Paths.BANNERS}`}>Manage Banners</Link>
            </div>
            <TurnOffBannerModal show={confirming} busy={busy} message={banner.message} onCancel={() => setConfirming(false)} onConfirm={turnOff} />
        </div>
    );
}
